import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const CATEGORIES = [
  { value: 'all', label: 'All Templates', icon: 'LayoutGrid', color: 'text-slate-600' },
  { value: 'ticket', label: 'Tickets', icon: 'ClipboardList', color: 'text-blue-600' },
  { value: 'quote', label: 'Quotes', icon: 'FileText', color: 'text-violet-600' },
  { value: 'invoice', label: 'Invoices', icon: 'DollarSign', color: 'text-emerald-600' },
  { value: 'work_order', label: 'Work Orders', icon: 'Wrench', color: 'text-amber-600' },
  { value: 'other', label: 'Other', icon: 'File', color: 'text-slate-500' },
];

const CATEGORY_COLORS = {
  ticket: 'bg-blue-50 text-blue-700 border-blue-200',
  quote: 'bg-violet-50 text-violet-700 border-violet-200',
  invoice: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  work_order: 'bg-amber-50 text-amber-700 border-amber-200',
  other: 'bg-slate-50 text-slate-700 border-slate-200',
};

const VARIABLE_TYPES = ['text', 'date', 'number', 'currency', 'email', 'textarea'];

const extractVariables = (content) => {
  const regex = /\{\{([^}]+)\}\}/g;
  const found = [];
  let match;
  while ((match = regex?.exec(content)) !== null) {
    const varName = match?.[1]?.trim();
    if (!found?.includes(varName)) found?.push(varName);
  }
  return found;
};

const substituteVariables = (content, values) => {
  let result = content;
  Object.entries(values)?.forEach(([key, val]) => {
    result = result?.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), val || `{{${key}}}`);
  });
  return result;
};

const TemplateModal = ({ template, onClose, onSave, orgId, userId }) => {
  const isEdit = !!template?.id;
  const [form, setForm] = useState({
    name: template?.name || '',
    description: template?.description || '',
    category: template?.category || 'ticket',
    content: template?.content || '',
    is_active: template?.is_active !== false,
    is_default: template?.is_default || false,
  });
  const [variables, setVariables] = useState(template?.variables || []);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('editor');
  const [previewValues, setPreviewValues] = useState({});

  const detectedVars = extractVariables(form?.content);

  useEffect(() => {
    const newVars = detectedVars?.map(name => {
      const existing = variables?.find(v => v?.name === name);
      return existing || { name, label: name?.replace(/_/g, ' ')?.replace(/\b\w/g, c => c?.toUpperCase()), type: 'text' };
    });
    setVariables(newVars);
  }, [form?.content]);

  const handleSave = async () => {
    if (!form?.name?.trim() || !form?.content?.trim()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        variables,
        organization_id: orgId,
        updated_by: userId,
        updated_at: new Date()?.toISOString(),
      };
      if (!isEdit) payload.created_by = userId;

      if (isEdit) {
        const { error } = await supabase?.from('document_templates')?.update(payload)?.eq('id', template?.id);
        if (error) throw error;
      } else {
        const { error } = await supabase?.from('document_templates')?.insert(payload);
        if (error) throw error;
      }
      onSave();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const previewContent = substituteVariables(form?.content, previewValues);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{isEdit ? 'Edit Template' : 'New Template'}</h2>
            <p className="text-sm text-gray-500 mt-0.5">Use {'{{variable_name}}'} syntax for dynamic fields</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <Icon name="X" size={18} className="text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="p-6 grid grid-cols-3 gap-4">
            <div className="col-span-2 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Template Name *</label>
                  <Input value={form?.name} onChange={e => setForm(f => ({ ...f, name: e?.target?.value }))} placeholder="e.g. Standard Service Ticket" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <select
                    value={form?.category}
                    onChange={e => setForm(f => ({ ...f, category: e?.target?.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {CATEGORIES?.filter(c => c?.value !== 'all')?.map(c => (
                      <option key={c?.value} value={c?.value}>{c?.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <Input value={form?.description} onChange={e => setForm(f => ({ ...f, description: e?.target?.value }))} placeholder="Brief description of this template" />
              </div>

              <div className="flex gap-2 border-b border-gray-200">
                {['editor', 'preview']?.map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2 text-sm font-medium capitalize border-b-2 transition-colors ${
                      activeTab === tab ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {activeTab === 'editor' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Template Content *</label>
                  <textarea
                    value={form?.content}
                    onChange={e => setForm(f => ({ ...f, content: e?.target?.value }))}
                    rows={14}
                    placeholder="Enter template content. Use {{variable_name}} for dynamic fields..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                </div>
              ) : (
                <div>
                  <div className="bg-gray-50 rounded-lg p-4 mb-3">
                    <p className="text-xs font-medium text-gray-600 mb-2">Fill in preview values:</p>
                    <div className="grid grid-cols-2 gap-2">
                      {detectedVars?.map(v => (
                        <div key={v}>
                          <label className="block text-xs text-gray-500 mb-0.5">{v}</label>
                          <input
                            type="text"
                            value={previewValues?.[v] || ''}
                            onChange={e => setPreviewValues(pv => ({ ...pv, [v]: e?.target?.value }))}
                            placeholder={`Enter ${v}`}
                            className="w-full px-2 py-1 border border-gray-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-lg p-4 min-h-[200px]">
                    <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans">{previewContent}</pre>
                  </div>
                </div>
              )}

              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form?.is_active} onChange={e => setForm(f => ({ ...f, is_active: e?.target?.checked }))} className="rounded" />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form?.is_default} onChange={e => setForm(f => ({ ...f, is_default: e?.target?.checked }))} className="rounded" />
                  <span className="text-sm text-gray-700">Set as Default</span>
                </label>
              </div>
            </div>

            <div className="space-y-3">
              <div className="bg-blue-50 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-1.5">
                  <Icon name="Braces" size={14} />
                  Detected Variables ({detectedVars?.length})
                </h4>
                {detectedVars?.length === 0 ? (
                  <p className="text-xs text-blue-600">No variables detected yet. Use {'{{variable_name}}'} in content.</p>
                ) : (
                  <div className="space-y-2">
                    {variables?.map((v, i) => (
                      <div key={v?.name} className="bg-white rounded-lg p-2 border border-blue-100">
                        <div className="flex items-center justify-between mb-1">
                          <code className="text-xs font-mono text-blue-700">{`{{${v?.name}}}`}</code>
                          <select
                            value={v?.type}
                            onChange={e => {
                              const updated = [...variables];
                              updated[i] = { ...updated?.[i], type: e?.target?.value };
                              setVariables(updated);
                            }}
                            className="text-xs border border-gray-200 rounded px-1 py-0.5 focus:outline-none"
                          >
                            {VARIABLE_TYPES?.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                        <input
                          type="text"
                          value={v?.label}
                          onChange={e => {
                            const updated = [...variables];
                            updated[i] = { ...updated?.[i], label: e?.target?.value };
                            setVariables(updated);
                          }}
                          placeholder="Display label"
                          className="w-full text-xs px-2 py-1 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Quick Insert</h4>
                <div className="flex flex-wrap gap-1">
                  {['customer_name', 'date', 'invoice_number', 'total_amount', 'technician_name', 'ticket_number']?.map(v => (
                    <button
                      key={v}
                      onClick={() => setForm(f => ({ ...f, content: f?.content + `{{${v}}}` }))}
                      className="text-xs bg-white border border-gray-200 rounded px-2 py-1 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-colors font-mono"
                    >
                      {`{{${v}}}`}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !form?.name?.trim() || !form?.content?.trim()}>
            {saving ? 'Saving...' : isEdit ? 'Update Template' : 'Create Template'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const DocumentTemplatesManager = () => {
  const { orgMembership, user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const orgId = orgMembership?.organization_id;
  const userId = user?.id;

  const showSuccess = (msg) => { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(''), 3000); };
  const showError = (msg) => { setErrorMsg(msg); setTimeout(() => setErrorMsg(''), 4000); };

  const fetchTemplates = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('document_templates')?.select('*')?.eq('organization_id', orgId)?.order('created_at', { ascending: false });
      if (error) throw error;
      setTemplates(data || []);
    } catch (err) {
      showError(err?.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleDelete = async (id) => {
    try {
      const { error } = await supabase?.from('document_templates')?.delete()?.eq('id', id);
      if (error) throw error;
      setTemplates(prev => prev?.filter(t => t?.id !== id));
      setDeleteConfirm(null);
      showSuccess('Template deleted successfully');
    } catch (err) {
      showError(err?.message || 'Failed to delete template');
    }
  };

  const handleToggleActive = async (template) => {
    try {
      const { error } = await supabase?.from('document_templates')?.update({ is_active: !template?.is_active, updated_at: new Date()?.toISOString() })?.eq('id', template?.id);
      if (error) throw error;
      setTemplates(prev => prev?.map(t => t?.id === template?.id ? { ...t, is_active: !t?.is_active } : t));
    } catch (err) {
      showError(err?.message || 'Failed to update template');
    }
  };

  const handleDuplicate = async (template) => {
    try {
      const { id, created_at, updated_at, usage_count, ...rest } = template;
      const { error } = await supabase?.from('document_templates')?.insert({
        ...rest,
        name: `${template?.name} (Copy)`,
        is_default: false,
        usage_count: 0,
        created_by: userId,
        updated_by: userId,
      });
      if (error) throw error;
      fetchTemplates();
      showSuccess('Template duplicated successfully');
    } catch (err) {
      showError(err?.message || 'Failed to duplicate template');
    }
  };

  const filtered = templates?.filter(t => {
    const matchCat = activeCategory === 'all' || t?.category === activeCategory;
    const matchSearch = !search || t?.name?.toLowerCase()?.includes(search?.toLowerCase()) || t?.description?.toLowerCase()?.includes(search?.toLowerCase());
    return matchCat && matchSearch;
  });

  const categoryCounts = CATEGORIES?.reduce((acc, cat) => {
    acc[cat.value] = cat?.value === 'all' ? templates?.length : templates?.filter(t => t?.category === cat?.value)?.length;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

          {/* Header */}
          <div className="flex items-start justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Document Templates</h1>
              <p className="text-gray-500 mt-1">Create and manage reusable templates with dynamic variable substitution</p>
            </div>
            <Button onClick={() => { setEditTemplate(null); setShowModal(true); }} className="flex items-center gap-2">
              <Icon name="Plus" size={16} />
              New Template
            </Button>
          </div>

          {/* Alerts */}
          {successMsg && (
            <div className="mb-4 flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-lg text-sm">
              <Icon name="CheckCircle" size={16} />{successMsg}
            </div>
          )}
          {errorMsg && (
            <div className="mb-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              <Icon name="AlertCircle" size={16} />{errorMsg}
            </div>
          )}

          <div className="flex gap-6">
            {/* Sidebar */}
            <div className="w-56 flex-shrink-0">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Categories</p>
                </div>
                <nav className="p-2">
                  {CATEGORIES?.map(cat => (
                    <button
                      key={cat?.value}
                      onClick={() => setActiveCategory(cat?.value)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                        activeCategory === cat?.value
                          ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon name={cat?.icon} size={15} className={activeCategory === cat?.value ? 'text-blue-600' : cat?.color} />
                        {cat?.label}
                      </div>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                        activeCategory === cat?.value ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                      }`}>{categoryCounts?.[cat?.value] || 0}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 min-w-0">
              <div className="mb-4">
                <div className="relative">
                  <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={e => setSearch(e?.target?.value)}
                    placeholder="Search templates..."
                    className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  />
                </div>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : filtered?.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-xl border border-gray-200">
                  <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="FileText" size={24} className="text-gray-400" />
                  </div>
                  <h3 className="text-gray-700 font-medium mb-1">No templates found</h3>
                  <p className="text-gray-400 text-sm mb-4">{search ? 'Try a different search term' : 'Create your first template to get started'}</p>
                  {!search && <Button onClick={() => { setEditTemplate(null); setShowModal(true); }}>Create Template</Button>}
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {filtered?.map(template => (
                    <div key={template?.id} className="bg-white rounded-xl border border-gray-200 hover:border-blue-200 hover:shadow-sm transition-all group">
                      <div className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs px-2 py-0.5 rounded-full border font-medium capitalize ${CATEGORY_COLORS?.[template?.category] || CATEGORY_COLORS?.other}`}>
                                {template?.category?.replace('_', ' ')}
                              </span>
                              {template?.is_default && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-medium">Default</span>
                              )}
                            </div>
                            <h3 className="font-semibold text-gray-900 truncate">{template?.name}</h3>
                            {template?.description && (
                              <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">{template?.description}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-1 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => { setEditTemplate(template); setShowModal(true); }}
                              className="p-1.5 rounded-lg hover:bg-blue-50 hover:text-blue-600 text-gray-400 transition-colors"
                              title="Edit"
                            >
                              <Icon name="Pencil" size={14} />
                            </button>
                            <button
                              onClick={() => handleDuplicate(template)}
                              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 transition-colors"
                              title="Duplicate"
                            >
                              <Icon name="Copy" size={14} />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(template?.id)}
                              className="p-1.5 rounded-lg hover:bg-red-50 hover:text-red-600 text-gray-400 transition-colors"
                              title="Delete"
                            >
                              <Icon name="Trash2" size={14} />
                            </button>
                          </div>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-3 mb-3">
                          <pre className="text-xs text-gray-600 whitespace-pre-wrap line-clamp-3 font-sans">{template?.content}</pre>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Icon name="Braces" size={12} />
                              {template?.variables?.length || 0} variables
                            </span>
                            <span className="flex items-center gap-1">
                              <Icon name="BarChart2" size={12} />
                              {template?.usage_count || 0} uses
                            </span>
                          </div>
                          <button
                            onClick={() => handleToggleActive(template)}
                            className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium transition-colors ${
                              template?.is_active
                                ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            }`}
                          >
                            <div className={`w-1.5 h-1.5 rounded-full ${template?.is_active ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                            {template?.is_active ? 'Active' : 'Inactive'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Template Modal */}
      {showModal && (
        <TemplateModal
          template={editTemplate}
          onClose={() => { setShowModal(false); setEditTemplate(null); }}
          onSave={() => { setShowModal(false); setEditTemplate(null); fetchTemplates(); showSuccess(editTemplate ? 'Template updated!' : 'Template created!'); }}
          orgId={orgId}
          userId={userId}
        />
      )}
      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Icon name="Trash2" size={18} className="text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Delete Template</h3>
                <p className="text-sm text-gray-500">This action cannot be undone.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentTemplatesManager;
